import { appConfirm } from "@/lib/appDialog";
import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import {
  Video, Search, Loader2, ExternalLink, Copy, Users, Calendar, Clock, X,
  RefreshCw, CheckCircle2, XCircle, AlertCircle, TrendingUp, PlayCircle,
  FileText, Trash2, ArrowRight, Download, Sparkles,
} from "lucide-react";

const STATUS_LABELS = {
  scheduled: { text: "مجدولة", cls: "bg-sky-50 text-sky-700 border-sky-200" },
  completed: { text: "منتهية", cls: "bg-emerald-50 text-emerald-700 border-emerald-200" },
  cancelled: { text: "ملغاة", cls: "bg-rose-50 text-rose-700 border-rose-200" },
};

function fmtDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("ar-SA", {
      dateStyle: "medium", timeStyle: "short",
    });
  } catch { return iso; }
}

function classifyTime(s) {
  if (s.status === "completed") return "past";
  if (s.status === "cancelled") return "cancelled";
  const now = Date.now();
  const at = s.scheduled_at ? new Date(s.scheduled_at).getTime() : now;
  const dur = (s.duration_minutes || 45) * 60000;
  if (at > now) return "upcoming";
  if (now >= at && now <= at + dur) return "live";
  return "past_unmarked";
}

export default function SessionsAdmin() {
  const navigate = useNavigate();
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all"); // all | upcoming | live | past | completed | cancelled | scheduled
  const [q, setQ] = useState("");
  const [detail, setDetail] = useState(null);
  const [migrating, setMigrating] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/sessions");
      setSessions(data.sessions || []);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const enriched = useMemo(() => sessions.map((s) => ({ ...s, _time: classifyTime(s) })), [sessions]);

  const counts = useMemo(() => ({
    all: enriched.length,
    upcoming: enriched.filter((s) => s._time === "upcoming").length,
    live: enriched.filter((s) => s._time === "live").length,
    scheduled: enriched.filter((s) => s.status === "scheduled").length,
    completed: enriched.filter((s) => s.status === "completed").length,
    cancelled: enriched.filter((s) => s.status === "cancelled").length,
    past: enriched.filter((s) => s._time === "past" || s._time === "past_unmarked").length,
    teams: enriched.filter((s) => s.meeting_provider === "microsoft_teams").length,
    pending_teams: enriched.filter((s) => s.meeting_provider !== "microsoft_teams").length,
  }), [enriched]);

  const filtered = useMemo(() => {
    let list = enriched;
    if (filter === "upcoming") list = list.filter((s) => s._time === "upcoming");
    else if (filter === "live") list = list.filter((s) => s._time === "live");
    else if (filter === "past") list = list.filter((s) => s._time === "past" || s._time === "past_unmarked");
    else if (filter === "completed") list = list.filter((s) => s.status === "completed");
    else if (filter === "cancelled") list = list.filter((s) => s.status === "cancelled");
    else if (filter === "scheduled") list = list.filter((s) => s.status === "scheduled");
    if (q.trim()) {
      const needle = q.trim().toLowerCase();
      list = list.filter((s) =>
        (s.title || "").toLowerCase().includes(needle) ||
        (s.mentor_name || "").toLowerCase().includes(needle) ||
        (s.student_name || "").toLowerCase().includes(needle)
      );
    }
    return list.sort((a, b) => new Date(b.scheduled_at) - new Date(a.scheduled_at));
  }, [enriched, filter, q]);

  const copyUrl = async (url) => {
    try { await navigator.clipboard.writeText(url); toast.success("نُسخ الرابط"); }
    catch { toast.error("تعذّر النسخ"); }
  };

  const migrateAll = async () => {
    if (!await appConfirm("ترحيل كل الجلسات القديمة (بانتظار Teams) إلى Microsoft Teams؟")) return;
    setMigrating(true);
    try {
      const { data } = await api.post("/admin/sessions/migrate-to-teams");
      toast.success(`تم ترحيل ${data.migrated} جلسة`);
      await load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setMigrating(false); }
  };

  const regenOne = async (sid) => {
    try {
      await api.post(`/sessions/${sid}/regenerate-meeting`);
      toast.success("تم تحديث الرابط إلى Teams");
      await load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const delOne = async (sid) => {
    if (!await appConfirm("حذف الجلسة نهائياً؟")) return;
    try {
      await api.delete(`/sessions/${sid}`);
      toast.success("حُذفت الجلسة");
      await load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const exportCsv = () => {
    if (!filtered.length) { toast.info("لا توجد جلسات للتصدير"); return; }
    const rows = [
      ["العنوان", "المعلّم", "الطالب", "الموعد", "المدة (د)", "الحالة", "المنصة", "الرابط"],
      ...filtered.map((s) => [
        s.title || "", s.mentor_name || "", s.student_name || "",
        s.scheduled_at || "", String(s.duration_minutes || 45),
        (STATUS_LABELS[s.status] || STATUS_LABELS.scheduled).text,
        s.meeting_provider === "microsoft_teams" ? "Microsoft Teams" : "بانتظار إنشاء Teams",
        s.join_url || "",
      ]),
    ];
    const csv = "\uFEFF" + rows.map((r) => r.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `sessions-${new Date().toISOString().split("T")[0]}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success(`تم تصدير ${filtered.length} جلسة`);
  };

  return (
    <div data-testid="sessions-admin" className="max-w-7xl mx-auto">
      {/* Top back bar */}
      <div className="mb-4 flex items-center justify-between flex-wrap gap-2">
        <button
          onClick={() => navigate("/dashboard")}
          className="inline-flex items-center gap-1.5 text-sm text-slate-600 hover:text-teal-700 font-medium"
          data-testid="sessions-admin-back"
        >
          <ArrowRight className="h-4 w-4" />
          العودة إلى لوحة التحكم
        </button>
        <div className="flex items-center gap-2">
          <button
            onClick={load}
            className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg bg-white border border-slate-300 text-slate-700 text-sm font-semibold hover:bg-cream-50"
            data-testid="sessions-refresh"
          >
            <RefreshCw className="h-4 w-4" /> تحديث
          </button>
          <button
            onClick={exportCsv}
            disabled={loading}
            className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg bg-white border border-slate-300 text-slate-700 text-sm font-semibold hover:bg-cream-50 disabled:opacity-60"
            data-testid="sessions-export"
          >
            <Download className="h-4 w-4" /> تصدير CSV
          </button>
          {counts.pending_teams > 0 && (
            <button
              onClick={migrateAll}
              disabled={migrating}
              className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg bg-amber-50 border border-amber-200 text-amber-800 text-sm font-semibold hover:bg-amber-100 disabled:opacity-60"
              data-testid="sessions-migrate-all"
            >
              {migrating ? <Loader2 className="h-4 w-4 animate-spin" /> : "🔄"}
              إنشاء Teams لـ {counts.pending_teams} جلسة
            </button>
          )}
        </div>
      </div>

      {/* Hero header with gradient */}
      <div className="mb-6 p-4 lg:p-8 rounded-2xl bg-gradient-to-l from-teal-900 via-teal-800 to-slate-900 text-white relative overflow-hidden card-shadow-lg" data-testid="sessions-hero">
        <div className="absolute inset-0 opacity-20 pointer-events-none">
          <div className="absolute -top-16 -end-16 h-64 w-64 rounded-full bg-amber-400/40 blur-3xl" />
          <div className="absolute -bottom-20 -start-16 h-72 w-72 rounded-full bg-teal-400/30 blur-3xl" />
        </div>
        <div className="relative flex items-start justify-between flex-wrap gap-4">
          <div>
            <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-white/10 backdrop-blur-md text-xs font-bold text-amber-200 border border-white/15">
              <Sparkles className="h-3 w-3" /> النظرة التشغيلية
            </div>
            <h1 className="mt-3 text-2xl lg:text-2xl font-heading font-black flex items-center gap-2">
              <Video className="h-6 w-6 text-amber-300" /> جلسات الإرشاد والتوجيه
            </h1>
            <p className="mt-2 text-teal-100/90 text-sm max-w-2xl">
              عرض شامل لجميع الجلسات القادمة والمنتهية والمُلغاة عبر كل المعلّمين والطلاب، مع تفاصيل كل جلسة وروابط الاجتماعات.
            </p>
          </div>
          <div className="flex items-center gap-4 text-center">
            <div>
              <div className="text-3xl lg:text-4xl font-heading font-black arabic-numerals text-amber-200" data-testid="hero-total">{counts.all}</div>
              <div className="text-[11px] text-teal-100/80 mt-0.5">إجمالي الجلسات</div>
            </div>
            <div className="h-10 w-px bg-white/20" />
            <div>
              <div className="text-3xl lg:text-4xl font-heading font-black arabic-numerals text-white">{counts.upcoming}</div>
              <div className="text-[11px] text-teal-100/80 mt-0.5">قادمة</div>
            </div>
            <div className="h-10 w-px bg-white/20" />
            <div>
              <div className="text-3xl lg:text-4xl font-heading font-black arabic-numerals text-emerald-300">{counts.live}</div>
              <div className="text-[11px] text-teal-100/80 mt-0.5">مباشر الآن</div>
            </div>
          </div>
        </div>
      </div>

      {/* KPI mini cards */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 mb-6" data-testid="sessions-kpi">
        {[
          { key: "all", label: "الكل", value: counts.all, icon: Video, tone: "slate" },
          { key: "upcoming", label: "قادمة", value: counts.upcoming, icon: Calendar, tone: "sky" },
          { key: "live", label: "الآن", value: counts.live, icon: PlayCircle, tone: "emerald" },
          { key: "scheduled", label: "مجدولة", value: counts.scheduled, icon: Clock, tone: "teal" },
          { key: "completed", label: "منتهية", value: counts.completed, icon: CheckCircle2, tone: "indigo" },
          { key: "cancelled", label: "ملغاة", value: counts.cancelled, icon: XCircle, tone: "rose" },
        ].map((c) => {
          const tones = {
            slate: "bg-slate-50 text-slate-700 border-slate-200",
            sky: "bg-sky-50 text-sky-700 border-sky-200",
            emerald: "bg-emerald-50 text-emerald-700 border-emerald-200",
            teal: "bg-teal-50 text-teal-700 border-teal-200",
            indigo: "bg-indigo-50 text-indigo-700 border-indigo-200",
            rose: "bg-rose-50 text-rose-700 border-rose-200",
          };
          const active = filter === c.key;
          return (
            <button
              key={c.key}
              onClick={() => setFilter(c.key)}
              className={`p-4 rounded-2xl border text-start transition-all ${active ? "border-teal-600 ring-2 ring-teal-500/25 bg-white" : "bg-white border-cream-200 hover:border-teal-300"}`}
              data-testid={`kpi-${c.key}`}
            >
              <div className={`h-8 w-8 rounded-lg grid place-items-center mb-2 border ${tones[c.tone]}`}>
                <c.icon className="h-4 w-4" strokeWidth={1.8} />
              </div>
              <div className="text-2xl font-heading font-black text-slate-900 arabic-numerals">{c.value}</div>
              <div className="text-xs text-slate-500 mt-0.5">{c.label}</div>
            </button>
          );
        })}
      </div>

      {/* Search */}
      <div className="flex items-center gap-2 mb-4">
        <div className="relative flex-1 max-w-md">
          <Search className="h-4 w-4 text-slate-400 absolute top-1/2 -translate-y-1/2 start-3" />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="ابحث بعنوان الجلسة، المعلّم، أو الطالب..."
            className="w-full ps-9 pe-3 py-2 rounded-lg border border-slate-300 bg-white text-sm focus:border-teal-500 focus:ring-2 focus:ring-teal-500/20 outline-none"
            data-testid="sessions-search"
          />
        </div>
        <div className="text-xs text-slate-500 arabic-numerals">{filtered.length} من {counts.all}</div>
      </div>

      {/* Table */}
      {loading ? (
        <div className="grid place-items-center py-16"><Loader2 className="h-8 w-8 animate-spin text-teal-700" /></div>
      ) : filtered.length === 0 ? (
        <div className="p-12 rounded-2xl bg-white border border-dashed border-cream-300 text-center">
          <Video className="h-8 w-8 text-slate-300 mx-auto mb-2" />
          <div className="text-slate-500 text-sm">لا توجد جلسات ضمن الفلتر الحالي</div>
        </div>
      ) : (
        <div className="rounded-2xl bg-white border border-cream-200 overflow-hidden card-shadow">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-cream-50 text-xs text-slate-600">
                <tr>
                  <th className="text-start px-4 py-3">العنوان</th>
                  <th className="text-start px-4 py-3">المعلّم</th>
                  <th className="text-start px-4 py-3">الطالب</th>
                  <th className="text-start px-4 py-3">الموعد</th>
                  <th className="text-start px-4 py-3">المدة</th>
                  <th className="text-start px-4 py-3">الحالة</th>
                  <th className="text-start px-4 py-3">المنصة</th>
                  <th className="text-end px-4 py-3">الإجراءات</th>
                </tr>
              </thead>
              <tbody data-testid="sessions-tbody">
                {filtered.map((s) => {
                  const st = STATUS_LABELS[s.status] || STATUS_LABELS.scheduled;
                  const isTeams = s.meeting_provider === "microsoft_teams";
                  const live = s._time === "live";
                  return (
                    <tr key={s.id} className={`border-t border-cream-100 hover:bg-cream-50/50 ${live ? "bg-emerald-50/30" : ""}`}>
                      <td className="px-4 py-3">
                        <button onClick={() => setDetail(s)} className="font-semibold text-slate-900 hover:text-teal-700 text-start" data-testid={`session-title-${s.id}`}>
                          {s.title}
                          {live && <span className="ms-2 inline-flex items-center gap-1 text-[10px] font-bold text-emerald-700 bg-emerald-100 px-1.5 py-0.5 rounded-full animate-pulse">● مباشر</span>}
                        </button>
                      </td>
                      <td className="px-4 py-3 text-slate-700">{s.mentor_name || "—"}</td>
                      <td className="px-4 py-3 text-slate-700">{s.student_name || "—"}</td>
                      <td className="px-4 py-3 text-slate-600 text-xs">{fmtDate(s.scheduled_at)}</td>
                      <td className="px-4 py-3 text-slate-600 arabic-numerals text-xs">{s.duration_minutes || 45} د</td>
                      <td className="px-4 py-3">
                        <span className={`px-2 py-1 rounded-full text-xs font-semibold border ${st.cls}`}>{st.text}</span>
                      </td>
                      <td className="px-4 py-3">
                        {isTeams ? (
                          <span className="inline-flex items-center gap-1 text-xs text-teal-700 font-semibold">
                            <Video className="h-3.5 w-3.5" /> Teams
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1 text-xs text-amber-700 font-semibold">
                            <AlertCircle className="h-3.5 w-3.5" /> بانتظار Teams
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center justify-end gap-1">
                          {s.join_url && (
                            <a href={s.join_url} target="_blank" rel="noreferrer" className="p-2 rounded-lg text-teal-700 hover:bg-teal-50" title="فتح الاجتماع" data-testid={`open-${s.id}`}>
                              <ExternalLink className="h-4 w-4" />
                            </a>
                          )}
                          {s.join_url && (
                            <button onClick={() => copyUrl(s.join_url)} className="p-2 rounded-lg text-slate-600 hover:bg-slate-100" title="نسخ الرابط">
                              <Copy className="h-4 w-4" />
                            </button>
                          )}
                          {!isTeams && (
                            <button onClick={() => regenOne(s.id)} className="p-2 rounded-lg text-amber-700 hover:bg-amber-50" title="تحويل إلى Teams" data-testid={`regen-${s.id}`}>
                              🔄
                            </button>
                          )}
                          <button onClick={() => setDetail(s)} className="p-2 rounded-lg text-slate-600 hover:bg-slate-100" title="التفاصيل" data-testid={`details-${s.id}`}>
                            <FileText className="h-4 w-4" />
                          </button>
                          <button onClick={() => delOne(s.id)} className="p-2 rounded-lg text-red-600 hover:bg-red-50" title="حذف">
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {detail && (
        <SessionDetail session={detail} onClose={() => setDetail(null)} />
      )}
    </div>
  );
}

function SessionDetail({ session, onClose }) {
  const st = STATUS_LABELS[session.status] || STATUS_LABELS.scheduled;
  const isTeams = session.meeting_provider === "microsoft_teams";
  return (
    <div className="fixed inset-0 z-50 bg-slate-900/60 backdrop-blur-sm grid place-items-center p-4" onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="w-full max-w-2xl bg-white rounded-2xl shadow-2xl border border-slate-200 max-h-[92vh] flex flex-col" data-testid="session-detail-modal">
        <div className="px-4 py-3 border-b border-slate-200 flex items-start justify-between gap-3">
          <div>
            <div className="text-xs font-bold uppercase tracking-widest text-teal-700">تفاصيل الجلسة</div>
            <h3 className="font-heading font-black text-slate-900 text-xl mt-1">{session.title}</h3>
          </div>
          <button onClick={onClose} className="p-2 rounded-lg hover:bg-slate-100"><X className="h-4 w-4" /></button>
        </div>
        <div className="p-4 overflow-y-auto space-y-4 text-sm">
          <div className="grid sm:grid-cols-2 gap-3">
            <Info label="الحالة"><span className={`px-2 py-0.5 rounded-full text-xs font-semibold border ${st.cls}`}>{st.text}</span></Info>
            <Info label="المنصة">
              {isTeams ? (
                <span className="inline-flex items-center gap-1 text-teal-700 font-semibold text-sm"><Video className="h-3.5 w-3.5" /> Microsoft Teams</span>
              ) : (
                <span className="inline-flex items-center gap-1 text-amber-700 font-semibold text-sm"><AlertCircle className="h-3.5 w-3.5" /> بانتظار Teams</span>
              )}
            </Info>
            <Info label="المعلّم">{session.mentor_name || "—"}</Info>
            <Info label="الطالب">{session.student_name || "—"}</Info>
            <Info label="الموعد">{fmtDate(session.scheduled_at)}</Info>
            <Info label="المدة"><span className="arabic-numerals">{session.duration_minutes || 45}</span> دقيقة</Info>
          </div>
          {session.agenda && <Info label="الأجندة"><div className="whitespace-pre-line">{session.agenda}</div></Info>}
          {session.notes && <Info label="الملاحظات"><div className="whitespace-pre-line">{session.notes}</div></Info>}
          {session.summary && <Info label="ملخص الجلسة"><div className="whitespace-pre-line">{session.summary}</div></Info>}
          {session.challenges && <Info label="التحديات"><div className="whitespace-pre-line">{session.challenges}</div></Info>}
          {session.recommendations && <Info label="التوصيات"><div className="whitespace-pre-line">{session.recommendations}</div></Info>}
          {session.next_steps && <Info label="الخطوات التالية"><div className="whitespace-pre-line">{session.next_steps}</div></Info>}
          {session.mentor_rating && <Info label="تقييم المعلّم"><span className="arabic-numerals">{session.mentor_rating}</span>/5 ⭐</Info>}
          {session.join_url && (
            <div>
              <div className="text-xs font-semibold text-slate-600 mb-1.5">رابط الاجتماع</div>
              <div className="p-3 rounded-lg bg-slate-50 border border-slate-200 text-xs text-slate-700 font-mono break-all" dir="ltr">
                {session.join_url}
              </div>
            </div>
          )}
          {session.meeting_id && (
            <div className="text-[11px] text-slate-500 font-mono break-all" dir="ltr">
              meeting_id: {session.meeting_id}
            </div>
          )}
        </div>
        <div className="px-4 py-3 border-t border-slate-200 flex items-center justify-between gap-2">
          {session.join_url && (
            <a href={session.join_url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-teal-700 text-white text-sm font-semibold hover:bg-teal-600" data-testid="detail-open-meeting">
              <ExternalLink className="h-4 w-4" /> فتح الاجتماع
            </a>
          )}
          <button onClick={onClose} className="ms-auto px-5 py-2 rounded-lg bg-slate-100 text-slate-700 font-semibold hover:bg-slate-200 text-sm">إغلاق</button>
        </div>
      </div>
    </div>
  );
}

function Info({ label, children }) {
  return (
    <div className="p-3 rounded-xl bg-cream-50 border border-cream-200">
      <div className="text-xs text-slate-500 mb-1">{label}</div>
      <div className="text-sm text-slate-900">{children}</div>
    </div>
  );
}
